import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Label, Textarea, ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import {
  TAXONOMY_MARKET_OPTIONS,
  TAXONOMY_PLATFORM_OPTIONS,
  useCreateTaxonomy,
  type Taxonomy,
  type TaxonomyScope,
} from '@/entities/taxonomy';
import { ImportFilePicker } from '@/entities/taxonomy-import';
import { FormSheet } from '@/shared/ui/FormSheet';
import { MultiSelectDropdown } from './MultiSelectDropdown';

export interface CreateTaxonomyModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (created: Taxonomy) => void;
  /** Starts a schema import from a file; "A file" is offered only when this is given. */
  onImport?: (input: { file: File; scope: TaxonomyScope }) => void;
  importing?: boolean;
  /** The server's refusal of the import, shown under the file. */
  importError?: string | null;
  /** Called on a new pick, so a refusal of the last file does not sit under the next one. */
  onClearImportError?: () => void;
}

type Start = 'blank' | 'file';

const DEFAULT_SCOPE: TaxonomyScope = 'workspace';
// Creation reason is implicit in the prototype (no field), but the audit trail
// requires one — send a stable default so the change-log stays complete.
// Written to the append-only change log, so it stays English on every writer.
// eslint-disable-next-line i18next/no-literal-string -- persisted audit reason, not copy
const CREATE_REASON = 'Schema created from Taxonomy administration';

/**
 * "New schema" modal. Captures name, scope, markets, platforms and an optional
 * description, then creates the taxonomy with its initial (Draft) version. Started from a file
 * instead, it takes only the scope and the file: the import agent drafts the rest.
 *
 * Chrome + footer come from the shared `FormSheet`, which shares its slot
 * contract with `FormDialog`, so spacing and the primary/secondary actions stay
 * consistent with the modals that remain. Market/platform are dropdown
 * multi-selects since the version stores arrays. "Import from Claravine" is
 * intentionally omitted.
 */
export function CreateTaxonomyModal(props: CreateTaxonomyModalProps) {
  const { t } = useTranslation('knowledge');
  const nameFieldId = useId();
  const marketFieldId = useId();
  const platformFieldId = useId();
  const descriptionFieldId = useId();

  const [name, setName] = useState('');
  const [scope, setScope] = useState<TaxonomyScope>(DEFAULT_SCOPE);
  const [markets, setMarkets] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [start, setStart] = useState<Start>('blank');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fromFile = Boolean(props.onImport) && start === 'file';

  const createTaxonomy = useCreateTaxonomy({
    onSuccess: (created) => {
      props.onCreated?.(created);
      props.onClose();
    },
  });

  // Reset the form each time the modal (re)opens so a prior draft never leaks.
  useEffect(() => {
    if (props.open) {
      setName('');
      setScope(DEFAULT_SCOPE);
      setMarkets([]);
      setPlatforms([]);
      setDescription('');
      setStart('blank');
      setFile(null);
      setFileError(null);
    }
  }, [props.open]);

  const trimmedName = name.trim();
  const canCreate =
    trimmedName.length > 0 &&
    markets.length > 0 &&
    platforms.length > 0 &&
    !createTaxonomy.isPending;

  const handleCreate = () => {
    if (!canCreate) return;
    createTaxonomy.mutate({
      name: trimmedName,
      scope,
      market: markets,
      platform: platforms,
      ...(description.trim() ? { description: description.trim() } : {}),
      reason: CREATE_REASON,
    });
  };

  const canImport = Boolean(file) && !fileError && !props.importing;
  const handleImport = () => {
    if (!file || !canImport) return;
    props.onImport?.({ file, scope });
  };

  const handleOpenChange = (next: boolean) => {
    if (createTaxonomy.isPending || props.importing) return;
    if (!next) props.onClose();
  };

  return (
    <FormSheet
      className="w-[35rem]"
      open={props.open}
      onOpenChange={handleOpenChange}
      title={t('taxonomy.createTaxonomyModal.newSchema')}
    >
      <FormSheet.Body className="flex flex-col gap-4">
        {props.onImport && (
          <div className="flex flex-col gap-1.5">
            <Label id={`${nameFieldId}-start`}>{t('taxonomy.createTaxonomyModal.startFrom')}</Label>
            <ToggleGroup
              aria-labelledby={`${nameFieldId}-start`}
              type="single"
              value={start}
              onValueChange={(value) => value && setStart(value as Start)}
              variant="outline"
              size="sm"
              className="grid w-full grid-cols-2"
            >
              {/* eslint-disable-next-line i18next/no-literal-string -- start key, not copy */}
              <ToggleGroupItem value="blank">
                {t('taxonomy.createTaxonomyModal.blank')}
              </ToggleGroupItem>
              {/* eslint-disable-next-line i18next/no-literal-string -- start key, not copy */}
              <ToggleGroupItem value="file">
                {t('taxonomy.createTaxonomyModal.aFile')}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        )}

        {!fromFile && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={nameFieldId}>
              {t('taxonomy.createTaxonomyModal.schemaName')}{' '}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id={nameFieldId}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('taxonomy.createTaxonomyModal.enterSchemaName')}
              maxLength={255}
              className="bg-input"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a modal the user just opened to type into
              autoFocus
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <Label id={`${nameFieldId}-scope`}>{t('taxonomy.createTaxonomyModal.scope')}</Label>
          <ToggleGroup
            aria-labelledby={`${nameFieldId}-scope`}
            type="single"
            value={scope}
            onValueChange={(value) => value && setScope(value as TaxonomyScope)}
            variant="outline"
            size="sm"
            className="grid w-full grid-cols-2"
          >
            {/* eslint-disable-next-line i18next/no-literal-string -- TaxonomyScope wire enum */}
            <ToggleGroupItem value="workspace">
              {t('taxonomy.createTaxonomyModal.workspace')}
            </ToggleGroupItem>
            {/* eslint-disable-next-line i18next/no-literal-string -- TaxonomyScope wire enum */}
            <ToggleGroupItem value="organization">
              {t('taxonomy.createTaxonomyModal.org')}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {fromFile && (
          <ImportFilePicker
            file={file}
            error={fileError ?? props.importError ?? null}
            onPick={(picked, error) => {
              setFile(picked);
              setFileError(error);
              props.onClearImportError?.();
            }}
            documents
            disabled={props.importing}
          />
        )}

        {!fromFile && (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={marketFieldId}>
                {t('taxonomy.createTaxonomyModal.market')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <MultiSelectDropdown
                id={marketFieldId}
                value={markets}
                onChange={setMarkets}
                options={TAXONOMY_MARKET_OPTIONS}
                placeholder={t('taxonomy.createTaxonomyModal.selectMarkets')}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={platformFieldId}>
                {t('taxonomy.createTaxonomyModal.platform')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <MultiSelectDropdown
                id={platformFieldId}
                value={platforms}
                onChange={setPlatforms}
                options={TAXONOMY_PLATFORM_OPTIONS}
                placeholder={t('taxonomy.createTaxonomyModal.selectPlatforms')}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={descriptionFieldId}>
                {t('taxonomy.createTaxonomyModal.description')}
              </Label>
              <Textarea
                id={descriptionFieldId}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('taxonomy.createTaxonomyModal.shareAnyAdditionalContextSchema')}
                rows={3}
                maxLength={2000}
                className="bg-input"
              />
            </div>
          </>
        )}
      </FormSheet.Body>

      <FormSheet.Footer
        primary={
          fromFile
            ? {
                label: t('taxonomy.createTaxonomyModal.readFile'),
                onClick: handleImport,
                disabled: !canImport,
                loading: Boolean(props.importing),
              }
            : {
                label: t('taxonomy.createTaxonomyModal.create'),
                onClick: handleCreate,
                disabled: !canCreate,
                loading: createTaxonomy.isPending,
              }
        }
        secondary={{
          label: t('taxonomy.createTaxonomyModal.cancel'),
          onClick: props.onClose,
        }}
      />
    </FormSheet>
  );
}

CreateTaxonomyModal.displayName = 'CreateTaxonomyModal';
